import React from 'react';
import { motion } from 'framer-motion';
interface DataPoint {
  label: string;
  value: number;
  color?: string;
  suffix?: string;
}
interface BarChartProps {
  data: DataPoint[];
  max?: number;
  className?: string;
  height?: number;
}
export function BarChart({
  data,
  max,
  className = '',
  height = 200
}: BarChartProps) {
  const maxValue = max || Math.max(...data.map(d => d.value)) * 1.1;
  return <div className={`w-full ${className}`} style={{
    height: `${height}px`
  }} data-id="element-5275">
      <div className="flex h-full items-end justify-between gap-2 sm:gap-4" data-id="element-5276">
        {data.map((point, index) => {
        const heightPercentage = point.value / maxValue * 100;
        return <div key={index} className="flex flex-col items-center justify-end h-full flex-1 group" data-id="element-5277">
              <div className="relative w-full flex justify-center items-end h-[85%]" data-id="element-5278">
                <motion.div initial={{
              height: 0
            }} whileInView={{
              height: `${heightPercentage}%`
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              delay: index * 0.1,
              ease: 'easeOut'
            }} className={`w-full max-w-[40px] rounded-t-lg ${point.color || 'bg-primary-500'} opacity-80 group-hover:opacity-100 transition-opacity relative`} data-id="element-5279">
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-charcoal-900 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none" data-id="element-5280">
                    {point.value}
                    {point.suffix || ''}
                  </div>
                </motion.div>
              </div>
              <div className="h-[15%] flex items-start justify-center pt-2" data-id="element-5281">
                <span className="text-[10px] sm:text-xs font-bold text-charcoal-500 text-center leading-tight" data-id="element-5282">
                  {point.label}
                </span>
              </div>
            </div>;
      })}
      </div>
    </div>;
}
interface PieChartProps {
  data: DataPoint[];
  size?: number;
  className?: string;
  donut?: boolean;
}
export function PieChart({
  data,
  size = 200,
  className = '',
  donut = true
}: PieChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;
  return <div className={`relative flex items-center justify-center ${className}`} style={{
    width: size,
    height: size
  }} data-id="element-5283">
      <svg viewBox="0 0 100 100" className="transform -rotate-90 w-full h-full" data-id="element-5284">
        {data.map((item, index) => {
        const percentage = item.value / total * 100;
        const angle = percentage / 100 * 360;
        const radius = 40;
        const circumference = 2 * Math.PI * radius;
        const dashArray = percentage / 100 * circumference;
        const gap = circumference - dashArray;
        const offset = (100 - currentAngle / 360 * 100) / 100 * circumference * -1;
        // Store current angle for next iteration
        const prevAngle = currentAngle;
        currentAngle += angle;
        return <motion.circle key={index} cx="50" cy="50" r={radius} fill="transparent" stroke={item.color || `hsl(${index * 60}, 70%, 50%)`} strokeWidth={donut ? 15 : 40} strokeDasharray={`${dashArray} ${gap}`} strokeDashoffset={-1 * (prevAngle / 360) * circumference} initial={{
          strokeDasharray: `0 ${circumference}`
        }} whileInView={{
          strokeDasharray: `${dashArray} ${gap}`
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          delay: index * 0.2,
          ease: 'easeOut'
        }} className="hover:opacity-80 transition-opacity cursor-pointer" data-id="element-5285" />;
      })}
      </svg>

      {donut && <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" data-id="element-5286">
          <span className="text-2xl font-bold text-charcoal-900" data-id="element-5287">{total}g</span>
          <span className="text-xs text-charcoal-500 font-medium uppercase" data-id="element-5288">
            Total
          </span>
        </div>}
    </div>;
}